import React from 'react';
import { useAuth } from '../contexts/auth-context';
import { addToast } from '@heroui/react';
import {
  getUserProfile,
  updateUserProfile,
  changePassword,
  getUserAddresses,
  addAddress,
  updateAddress,
  deleteAddress,
  setDefaultAddress,
  getUserOrders,
  getOrderDetails,
  cancelOrder,
  getLoyaltyData,
  getWishlist,
  addToWishlist,
  removeFromWishlist,
  getRecommendations,
  getPromotions,
  getUserReviews,
  addReview,
  updateReview,
  deleteReview,
  ProfileData,
  Address,
  Order,
  LoyaltyData,
  WishlistItem,
  Recommendation,
  Promotion,
  Review
} from '../services/user-service';

// Helper to normalize address object returned by server to client Address shape
const mapServerAddress = (a: any) => {
  const fullFromData = a.fullAddress || a.address || a.address_full || a.formatted || a.display || a.unrestricted_value || a.value || a.location || a.address_full_text || a.addressText || a.addr || a.address_line || a.address_line_1 || a.address1 || a.label;
  const constructed = [a.city, a.street, a.house, a.apartment].filter(Boolean).join(', ');
  return {
    id: a.id,
    title: a.label || a.title || '',
    label: a.label || a.title || '',
    fullAddress: fullFromData || constructed,
    isDefault: !!(a.is_primary || a.isPrimary || a.is_default),
    city: a.city || '',
    street: a.street || '',
    house: a.house || '',
    apartment: a.apartment || a.flat || '',
    floor: a.floor || '',
    intercom: a.intercom || '',
    private_house: !!a.private_house,
    zipCode: a.postal_code || a.zipCode || ''
  };
};

// Mock loyalty data for development
const mockLoyaltyData = {
  points: 2450,
  level: "Серебряный",
  nextLevel: "Золотой",
  nextLevelProgress: 65,
  pointsToNextLevel: 3550,
  totalEarned: 7650,
  spentThisMonth: 1200,
  expirationDate: "31.12.2023",
  historyFilter: 'all', // 'all' | 'earned' | 'spent'
  earningOptions: [
    {
      icon: "lucide:shopping-bag",
      title: "Покупки",
      description: "5% от суммы заказа",
      points: "5%"
    },
    {
      icon: "lucide:star",
      title: "Отзывы",
      description: "За отзыв с фото",
      points: 50
    },
    {
      icon: "lucide:cake",
      title: "День рождения",
      description: "Ежегодный бонус",
      points: 500
    },
    {
      icon: "lucide:users",
      title: "Приглашение друга",
      description: "За регистрацию друга",
      points: 300
    },
    {
      icon: "lucide:instagram",
      title: "Соц. сети",
      description: "За упоминание в Instagram",
      points: 200
    },
    {
      icon: "lucide:gift",
      title: "Акции",
      description: "Специальные акции",
      points: "1-1000"
    }
  ],
  history: [
    {
      date: "15.10.2023",
      description: "Начисление баллов за заказ",
      orderNumber: "103788",
      points: 245,
      status: "processed"
    },
    {
      date: "05.10.2023",
      description: "Списание баллов в счет оплаты заказа",
      orderNumber: "103641",
      points: -500,
      status: "processed"
    },
    {
      date: "01.10.2023",
      description: "Бонус в честь дня рождения",
      points: 500,
      status: "processed"
    },
    {
      date: "25.09.2023",
      description: "Начисление за отзыв с фото",
      points: 50,
      status: "processed"
    },
    {
      date: "20.09.2023",
      description: "Начисление баллов за заказ",
      orderNumber: "103542",
      points: 290,
      status: "processed"
    },
    {
      date: "15.09.2023",
      description: "Начисление за приглашение друга",
      points: 300,
      status: "processed"
    },
    {
      date: "10.09.2023",
      description: "Начисление баллов за заказ",
      orderNumber: "103478",
      points: 175,
      status: "processed"
    },
    {
      date: "01.09.2023",
      description: "Списание баллов в счет оплаты заказа",
      orderNumber: "103412",
      points: -700,
      status: "processed"
    },
    {
      date: "15.08.2023",
      description: "Начисление за публикацию в Instagram",
      points: 200,
      status: "processed"
    },
    {
      date: "01.08.2023",
      description: "Приветственные баллы за регистрацию",
      points: 100,
      status: "processed"
    }
  ]
};

// Central hook for managing all user-related data
export const useUserData = () => {
  const { isAuthenticated, user } = useAuth();
  
  // State for profile data
  const [profile, setProfile] = React.useState<ProfileData | null>(null);
  
  // State for addresses
  const [addresses, setAddresses] = React.useState<Address[]>([]);
  
  // State for orders
  const [orders, setOrders] = React.useState<Order[]>([]);
  const [selectedOrder, setSelectedOrder] = React.useState<Order | null>(null);
  
  // State for loyalty
  const [loyaltyData, setLoyaltyData] = React.useState<LoyaltyData | null>(null);
  
  // State for wishlist
  const [wishlistItems, setWishlistItems] = React.useState<WishlistItem[]>([]);
  
  // State for recommendations
  const [recommendations, setRecommendations] = React.useState<Recommendation[]>([]);
  
  // State for promotions
  const [promotions, setPromotions] = React.useState<Promotion[]>([]);
  
  // State for reviews
  const [reviews, setReviews] = React.useState<Review[]>([]);
  
  // Loading states
  const [isLoadingProfile, setIsLoadingProfile] = React.useState(false);
  const [isLoadingAddresses, setIsLoadingAddresses] = React.useState(false);
  const [isLoadingOrders, setIsLoadingOrders] = React.useState(false);
  const [isLoadingLoyalty, setIsLoadingLoyalty] = React.useState(false);
  const [isLoadingWishlist, setIsLoadingWishlist] = React.useState(false);
  const [isLoadingRecommendations, setIsLoadingRecommendations] = React.useState(false);
  const [isLoadingPromotions, setIsLoadingPromotions] = React.useState(false);
  const [isLoadingReviews, setIsLoadingReviews] = React.useState(false);
  
  // Action loading states
  const [isSavingProfile, setIsSavingProfile] = React.useState(false);
  const [isChangingPassword, setIsChangingPassword] = React.useState(false);
  const [isSavingAddress, setIsSavingAddress] = React.useState(false);
  const [isDeletingAddress, setIsDeletingAddress] = React.useState(false);
  const [isCancelingOrder, setIsCancelingOrder] = React.useState(false);
  const [isAddingToWishlist, setIsAddingToWishlist] = React.useState(false);
  const [isRemovingFromWishlist, setIsRemovingFromWishlist] = React.useState(false);
  const [isSavingReview, setIsSavingReview] = React.useState(false);
  const [isDeletingReview, setIsDeletingReview] = React.useState(false);
  
  // Error states
  const [profileError, setProfileError] = React.useState<Error | null>(null);
  const [addressesError, setAddressesError] = React.useState<Error | null>(null);
  const [ordersError, setOrdersError] = React.useState<Error | null>(null);
  const [loyaltyError, setLoyaltyError] = React.useState<Error | null>(null);
  const [wishlistError, setWishlistError] = React.useState<Error | null>(null);
  const [recommendationsError, setRecommendationsError] = React.useState<Error | null>(null);
  const [promotionsError, setPromotionsError] = React.useState<Error | null>(null);
  const [reviewsError, setReviewsError] = React.useState<Error | null>(null);

  // Fetch profile data
  const fetchProfile = React.useCallback(async () => {
    if (!isAuthenticated) return;
    
    setIsLoadingProfile(true);
    setProfileError(null);
    
    try {
      const data = await getUserProfile();
      setProfile(data);
      // If backend includes addresses inside profile response, populate addresses state
      // Support common shapes: data.addresses or data.user_addresses
      const maybeAddresses = (data as any)?.addresses || (data as any)?.user_addresses || null;
      if (Array.isArray(maybeAddresses)) {
        // Normalize server address objects to client shape
        setAddresses(maybeAddresses.map((a: any) => mapServerAddress(a)) as Address[]);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      setProfileError(error instanceof Error ? error : new Error('Failed to fetch profile'));
      addToast({
        title: "Ошибка",
        description: "Не удалось загрузить данные профиля",
        color: "danger"
      });
    } finally {
      setIsLoadingProfile(false);
    }
  }, [isAuthenticated]);

  // Update profile data
  const saveProfile = React.useCallback(async (data: ProfileData) => {
    if (!isAuthenticated) return;
    
    setIsSavingProfile(true);
    
    try {
      const updatedProfile = await updateUserProfile(data);
      setProfile(updatedProfile);
      
  // Note: auth context is updated elsewhere; ensure caller refreshes if needed
      // If updated profile contains addresses, refresh local addresses state
      const maybeAddresses = (updatedProfile as any)?.addresses || (updatedProfile as any)?.user_addresses || null;
      if (Array.isArray(maybeAddresses)) {
        setAddresses(maybeAddresses as Address[]);
      }
      
      addToast({
        title: "Успешно",
        description: "Профиль успешно обновлен",
        color: "success"
      });
      
      return true;
    } catch (error) {
      console.error('Error updating profile:', error);
      addToast({
        title: "Ошибка",
        description: "Не удалось обновить профиль",
        color: "danger"
      });
      return false;
    } finally {
      setIsSavingProfile(false);
    }
  }, [isAuthenticated]);

  // Change password
  const updatePassword = React.useCallback(async (currentPassword: string, newPassword: string, newPasswordConfirm?: string) => {
    if (!isAuthenticated) return { success: false };

    setIsChangingPassword(true);

    try {
      await changePassword(currentPassword, newPassword, newPasswordConfirm);

      addToast({
        title: "Успешно",
        description: "Пароль успешно изменен",
        color: "success"
      });

      return { success: true };
    } catch (err: any) {
      console.error('Error changing password:', err);

      // If service attached validation details, return them to the caller for field-level display
      if (err?.details) {
        return { success: false, errors: err.details };
      }

      addToast({
        title: "Ошибка",
        description: "Не удалось изменить пароль. Проверьте правильность текущего пароля",
        color: "danger"
      });

      return { success: false };
    } finally {
      setIsChangingPassword(false);
    }
  }, [isAuthenticated]);

  // Fetch addresses
  const fetchAddresses = React.useCallback(async () => {
    if (!isAuthenticated) return;
    
    setIsLoadingAddresses(true);
    setAddressesError(null);
    
    try {
      const data = await getUserAddresses();
      // Normalize server address objects
      setAddresses((data || []).map((a: any) => mapServerAddress(a)) as Address[]);
    } catch (error) {
      console.error('Error fetching addresses:', error);
      setAddressesError(error instanceof Error ? error : new Error('Failed to fetch addresses'));
      addToast({
        title: "Ошибка",
        description: "Не удалось загрузить адреса",
        color: "danger"
      });
    } finally {
      setIsLoadingAddresses(false);
    }
  }, [isAuthenticated]);

  // Save address (add new or update existing)
  const saveAddress = React.useCallback(async (address: Address | Omit<Address, 'id'>) => {
    if (!isAuthenticated) return null;
    
    setIsSavingAddress(true);
    
    try {
      let updatedAddress: any;

      if ('id' in address) {
        // Update existing address
        updatedAddress = await updateAddress(address as Address);
        const mapped = mapServerAddress(updatedAddress);

        // If the updated address is primary, clear primary flag on others
        if (mapped.isDefault) {
          setAddresses(prev => prev.map(a => a.id === mapped.id ? mapped : { ...a, isDefault: false }));
        } else {
          setAddresses(prev => prev.map(a => a.id === mapped.id ? mapped : a));
        }

        addToast({ title: "Успешно", description: "Адрес успешно обновлен", color: "success" });
        return mapped;
      } else {
        // Add new address
        updatedAddress = await addAddress(address);
        const mapped = mapServerAddress(updatedAddress);

        setAddresses(prev => {
          if (mapped.isDefault) {
            // unset primary on others
            return [...prev.map(a => ({ ...a, isDefault: false })), mapped];
          }
          return [...prev, mapped];
        });

        addToast({ title: "Успешно", description: "Адрес успешно добавлен", color: "success" });
        return mapped;
      }
    } catch (error) {
      console.error('Error saving address:', error);
      addToast({
        title: "Ошибка",
        description: "Не удалось сохранить адрес",
        color: "danger"
      });
      return null;
    } finally {
      setIsSavingAddress(false);
    }
  }, [isAuthenticated]);

  // Remove address
  const removeAddress = React.useCallback(async (addressId: number) => {
    if (!isAuthenticated) return false;
    
    setIsDeletingAddress(true);
    
    try {
      await deleteAddress(addressId);
      
      // Remove from state
      setAddresses(prev => prev.filter(addr => addr.id !== addressId));
      
      addToast({
        title: "Успешно",
        description: "Адрес успешно удален",
        color: "success"
      });
      
      return true;
    } catch (error) {
      console.error('Error deleting address:', error);
      addToast({
        title: "Ошибка",
        description: "Не удалось удалить адрес",
        color: "danger"
      });
      return false;
    } finally {
      setIsDeletingAddress(false);
    }
  }, [isAuthenticated]);

  // Set address as default
  const makeAddressDefault = React.useCallback(async (addressId: number) => {
    if (!isAuthenticated) return false;
    
    try {
      await setDefaultAddress(addressId);
      
      // Update state to reflect the change
      setAddresses(prev => prev.map(addr => ({
        ...addr,
        isDefault: addr.id === addressId
      })));
      
      addToast({
        title: "Успешно",
        description: "Адрес по умолчанию изменен",
        color: "success"
      });
      
      return true;
    } catch (error) {
      console.error('Error setting default address:', error);
      addToast({
        title: "Ошибка",
        description: "Не удалось изменить адрес по умолчанию",
        color: "danger"
      });
      return false;
    }
  }, [isAuthenticated]);

  // Fetch orders
  const fetchOrders = React.useCallback(async () => {
    if (!isAuthenticated) return;
    
    setIsLoadingOrders(true);
    setOrdersError(null);
    
    try {
      const data = await getUserOrders();
      setOrders(data);
    } catch (error) {
      console.error('Error fetching orders:', error);
      setOrdersError(error instanceof Error ? error : new Error('Failed to fetch orders'));
      addToast({
        title: "Ошибка",
        description: "Не удалось загрузить заказы",
        color: "danger"
      });
    } finally {
      setIsLoadingOrders(false);
    }
  }, [isAuthenticated]);

  // Fetch single order details
  const fetchOrderDetails = React.useCallback(async (orderId: string) => {
    if (!isAuthenticated) return null;
    
    setIsLoadingOrders(true);
    
    try {
      const orderDetails = await getOrderDetails(orderId);
      setSelectedOrder(orderDetails);
      return orderDetails;
    } catch (error) {
      console.error('Error fetching order details:', error);
      addToast({
        title: "Ошибка",
        description: "Не удалось загрузить детали заказа",
        color: "danger"
      });
      return null;
    } finally {
      setIsLoadingOrders(false);
    }
  }, [isAuthenticated]);

  // Cancel order
  const handleCancelOrder = React.useCallback(async (orderId: string) => {
    if (!isAuthenticated) return false;
    
    setIsCancelingOrder(true);
    
    try {
      await cancelOrder(orderId);
      
      // Update order status in state
      setOrders(prev => prev.map(order => {
        if (order.id === orderId) {
          return {
            ...order,
            status: "cancelled" as const
          };
        }
        return order;
      }));
      
      // Also update selected order if it's currently viewed
      if (selectedOrder?.id === orderId) {
        setSelectedOrder({
          ...selectedOrder,
          status: "cancelled" as const
        });
      }
      
      addToast({
        title: "Успешно",
        description: "Заказ успешно отменен",
        color: "success"
      });
      
      return true;
    } catch (error) {
      console.error('Error canceling order:', error);
      addToast({
        title: "Ошибка",
        description: "Не удалось отменить заказ",
        color: "danger"
      });
      return false;
    } finally {
      setIsCancelingOrder(false);
    }
  }, [isAuthenticated, selectedOrder]);

  // Fetch loyalty data
  const fetchLoyaltyData = React.useCallback(async () => {
    if (!isAuthenticated) return;
    
    setIsLoadingLoyalty(true);
    setLoyaltyError(null);
    
    try {
      // В проде: раскомментировать и убрать мок-данные
      // const data = await getLoyaltyData();
      const data = mockLoyaltyData; // Мок-данные для разработки
      setLoyaltyData(data);
    } catch (error) {
      console.error('Error fetching loyalty data:', error);
      setLoyaltyError(error instanceof Error ? error : new Error('Failed to fetch loyalty data'));
      addToast({
        title: "Ошибка",
        description: "Не удалось загрузить данные по баллам лояльности",
        color: "danger"
      });
    } finally {
      setIsLoadingLoyalty(false);
    }
  }, [isAuthenticated]);

  // Fetch wishlist
  const fetchWishlist = React.useCallback(async () => {
    if (!isAuthenticated) return;
    
    setIsLoadingWishlist(true);
    setWishlistError(null);
    
    try {
      const data = await getWishlist();
      setWishlistItems(data);
    } catch (error) {
      console.error('Error fetching wishlist:', error);
      setWishlistError(error instanceof Error ? error : new Error('Failed to fetch wishlist'));
      addToast({
        title: "Ошибка",
        description: "Не удалось загрузить список избранного",
        color: "danger"
      });
    } finally {
      setIsLoadingWishlist(false);
    }
  }, [isAuthenticated]);

  // Add item to wishlist
  const handleAddToWishlist = React.useCallback(async (productId: number) => {
    if (!isAuthenticated) return false;
    
    setIsAddingToWishlist(true);
    
    try {
      await addToWishlist(productId);
      
      // Refresh wishlist
      await fetchWishlist();
      
      addToast({
        title: "Успешно",
        description: "Товар добавлен в избранное",
        color: "success"
      });
      
      return true;
    } catch (error) {
      console.error('Error adding to wishlist:', error);
      addToast({
        title: "Ошибка",
        description: "Не удалось добавить товар в избранное",
        color: "danger"
      });
      return false;
    } finally {
      setIsAddingToWishlist(false);
    }
  }, [isAuthenticated, fetchWishlist]);

  // Remove item from wishlist
  const handleRemoveFromWishlist = React.useCallback(async (productId: number) => {
    if (!isAuthenticated) return false;
    
    setIsRemovingFromWishlist(true);
    
    try {
      await removeFromWishlist(productId);
      
      // Remove from state
      setWishlistItems(prev => prev.filter(item => item.id !== productId));
      
      addToast({
        title: "Успешно",
        description: "Товар удален из избранного",
        color: "success"
      });
      
      return true;
    } catch (error) {
      console.error('Error removing from wishlist:', error);
      addToast({
        title: "Ошибка",
        description: "Не удалось удалить товар из избранного",
        color: "danger"
      });
      return false;
    } finally {
      setIsRemovingFromWishlist(false);
    }
  }, [isAuthenticated]);

  // Fetch recommendations
  const fetchRecommendations = React.useCallback(async () => {
    if (!isAuthenticated) return;
    
    setIsLoadingRecommendations(true);
    setRecommendationsError(null);
    
    try {
      const data = await getRecommendations();
      setRecommendations(data);
    } catch (error) {
      console.error('Error fetching recommendations:', error);
      setRecommendationsError(error instanceof Error ? error : new Error('Failed to fetch recommendations'));
      addToast({
        title: "Ошибка",
        description: "Не удалось загрузить рекомендации",
        color: "danger"
      });
    } finally {
      setIsLoadingRecommendations(false);
    }
  }, [isAuthenticated]);

  // Fetch promotions
  const fetchPromotions = React.useCallback(async () => {
    if (!isAuthenticated) return;
    
    setIsLoadingPromotions(true);
    setPromotionsError(null);
    
    try {
      const data = await getPromotions();
      setPromotions(data);
    } catch (error) {
      console.error('Error fetching promotions:', error);
      setPromotionsError(error instanceof Error ? error : new Error('Failed to fetch promotions'));
      addToast({
        title: "Ошибка",
        description: "Не удалось загрузить персональные акции",
        color: "danger"
      });
    } finally {
      setIsLoadingPromotions(false);
    }
  }, [isAuthenticated]);

  // Fetch reviews
  const fetchReviews = React.useCallback(async () => {
    if (!isAuthenticated) return;
    
    setIsLoadingReviews(true);
    setReviewsError(null);
    
    try {
      const data = await getUserReviews();
      setReviews(data);
    } catch (error) {
      console.error('Error fetching reviews:', error);
      setReviewsError(error instanceof Error ? error : new Error('Failed to fetch reviews'));
      addToast({
        title: "Ошибка",
        description: "Не удалось загрузить отзывы",
        color: "danger"
      });
    } finally {
      setIsLoadingReviews(false);
    }
  }, [isAuthenticated]);

  // Save review (add new or update existing)
  const saveReview = React.useCallback(async (params: {
    reviewId?: number;
    productId: number;
    rating: number;
    text: string;
  }) => {
    if (!isAuthenticated) {
      addToast({
        title: "Требуется авторизация",
        description: "Войдите в личный кабинет, чтобы оставить отзыв",
        color: "warning"
      });
      return null;
    }
    
    const { reviewId, productId, rating, text } = params;
    setIsSavingReview(true);
    
    try {
      let savedReview;
      
      if (reviewId) {
        // Update existing review
        savedReview = await updateReview(reviewId, rating, text);
        
        // Update state
        setReviews(prev => prev.map(review => 
          review.id === reviewId ? savedReview : review
        ));
        
        addToast({
          title: "Успешно",
          description: "Отзыв успешно обновлен",
          color: "success"
        });
      } else {
        // Add new review
        savedReview = await addReview(productId, rating, text);
        
        // Add to state
        setReviews(prev => [...prev, savedReview]);
        
        addToast({
          title: "Успешно",
          description: "Отзыв успешно добавлен",
          color: "success"
        });
      }
      
      return savedReview;
    } catch (error) {
      console.error('Error saving review:', error);
      addToast({
        title: "Ошибка",
        description: "Не удалось сохранить отзыв",
        color: "danger"
      });
      return null;
    } finally {
      setIsSavingReview(false);
    }
  }, [isAuthenticated]);

  // Delete review
  const removeReview = React.useCallback(async (reviewId: number) => {
    if (!isAuthenticated) return false;
    
    setIsDeletingReview(true);
    
    try {
      await deleteReview(reviewId);
      
      // Remove from state
      setReviews(prev => prev.filter(review => review.id !== reviewId));
      
      addToast({
        title: "Успешно",
        description: "Отзыв успешно удален",
        color: "success"
      });
      
      return true;
    } catch (error) {
      console.error('Error deleting review:', error);
      addToast({
        title: "Ошибка",
        description: "Не удалось удалить отзыв",
        color: "danger"
      });
      return false;
    } finally {
      setIsDeletingReview(false);
    }
  }, [isAuthenticated]);

  return {
    // Data
    profile,
    addresses,
    orders,
    selectedOrder,
    loyaltyData,
    wishlistItems,
    recommendations,
    promotions,
    reviews,
    
    // Loading states
    isLoadingProfile,
    isLoadingAddresses,
    isLoadingOrders,
    isLoadingLoyalty,
    isLoadingWishlist,
    isLoadingRecommendations,
    isLoadingPromotions,
    isLoadingReviews,
    isSavingProfile,
    isChangingPassword,
    isSavingAddress,
    isDeletingAddress,
    isCancelingOrder,
    isAddingToWishlist,
    isRemovingFromWishlist,
    isSavingReview,
    isDeletingReview,
    
    // Error states
    profileError,
    addressesError,
    ordersError,
    loyaltyError,
    wishlistError,
    recommendationsError,
    promotionsError,
    reviewsError,
    
    // Actions
    fetchProfile,
    saveProfile,
    updatePassword,
    fetchAddresses,
    saveAddress,
    removeAddress,
    makeAddressDefault,
    fetchOrders,
    fetchOrderDetails,
    handleCancelOrder,
    fetchLoyaltyData,
    fetchWishlist,
    handleAddToWishlist,
    handleRemoveFromWishlist,
    fetchRecommendations,
    fetchPromotions,
    fetchReviews,
    saveReview,
    removeReview,
    
    // Setters for direct state manipulation
    setSelectedOrder,
  };
};